import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { live } from 'lit-html/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import debounce from 'lodash-es/debounce';

import {
  CustomQuantityOptionDto,
  QuantityOptionDto,
  QuantityStepDto,
  StepEnum,
} from '@/api-sdk';
import { iconCheckmark } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import {
  baseStyles,
  formStyles,
  loadingStyles,
  quantityTableStyles,
} from '@/styles';
import {
  announceQuantityChange,
  getNumberValueFromString,
  getQuantityA11yName,
} from '@/utils';

import '../../../configuration/hsp/fg-hsp-label';

@customElement('fg-quantity-table')
@localized()
export class FgQuantityTable extends LitElement {
  @property({ type: Array })
  options: QuantityStepDto['quantityOptions'] = [];

  @property({ type: Object })
  customQuantityOption?: CustomQuantityOptionDto;

  @property({ type: Boolean })
  loading = true;

  @property({ type: String })
  a11yDescription = '';

  @state()
  customQuantity = { value: 0 };

  @state()
  prevCustomQuantity = { value: 0 };

  @state()
  firstInteracted = false;

  @state()
  private previousTotalPrice?: string | null;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  private MAX_QUANTITY_VALUE = 1000000;

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('customQuantityOption')) {
      if (
        this.customQuantityOption?.quantity &&
        this.customQuantityOption?.quantity > 0 &&
        this.customQuantityOption?.totalPrice !== null &&
        this.customQuantityOption?.totalPrice !== undefined &&
        this.customQuantityOption?.totalPrice !== this.previousTotalPrice
      ) {
        announceQuantityChange(
          this,
          this.configuratorContext.quantityCollapsible
            ? StepEnum.quantity_collapsible
            : StepEnum.quantity,
          {
            quantity: this.customQuantityOption?.quantity,
            fastDelivery: this.customQuantityOption?.hspAvailable,
            discountPercentage:
              this.customQuantityOption?.volumeDiscountPercentage,
            unitPrice: this.customQuantityOption?.unitPrice,
            totalPrice: this.customQuantityOption?.totalPrice,
          },
        );
      }

      this.previousTotalPrice = this.customQuantityOption?.totalPrice;
    }
  }

  renderCustomQuantityOption() {
    const { quantity, totalPrice, unitPrice, volumeDiscountPercentage } =
      this.customQuantityOption || {};

    const quantityInputValue =
      this.customQuantity.value > 0 ? this.customQuantity.value.toString() : '';

    const customQuantityOptionQuantity = quantity?.toString() || '';

    const { quantityCollapsible } = this.configuratorContext;

    return html`
      <tfoot>
        <tr
          class="${quantity ? 'active' : ''} ${this.loading ? 'loading' : ''}"
        >
          <td class="custom-quantity-input-td amount">
            <span class="checkmark-wrapper">
              <span
                class="icon-wrapper${classMap({
                  collapsible: Boolean(quantityCollapsible),
                })}"
              >
                <span
                  class="checkmark${classMap({
                    selected: (quantity ?? 0) > 0,
                  })}"
                >
                  ${iconCheckmark}
                </span>
              </span>

              <input
                class="custom-quantity-input"
                .placeholder=${t('labels.customQuantity') || ''}
                .value=${live(
                  this.firstInteracted
                    ? quantityInputValue
                    : customQuantityOptionQuantity,
                )}
                @input=${(event: KeyboardEvent) => {
                  const eventTarget = event.target as HTMLInputElement;
                  const value = parseFloat(eventTarget.value);
                  if (value > this.MAX_QUANTITY_VALUE) {
                    eventTarget.value =
                      this.prevCustomQuantity.value?.toString();
                    this.customQuantity = this.prevCustomQuantity;
                    return;
                  }
                  this.prevCustomQuantity = { value: isNaN(value) ? 0 : value };
                  this.setCustomQuantity(event);
                }}
              />
            </span>
          </td>
          <td class="custom-quantity-input-td">
            ${volumeDiscountPercentage
              ? html`
                  <span class="discount-percentage">
                    ${volumeDiscountPercentage}%
                  </span>
                `
              : html`
                  <span>-</span>
                `}
          </td>
          <td class="custom-quantity-input-td">
            <span>${unitPrice ?? '-'}</span>
          </td>
          <td class="custom-quantity-input-td">
            <span>${totalPrice ?? '-'}</span>
          </td>
        </tr>
      </tfoot>
    `;
  }

  render() {
    const { quantityCollapsible } = this.configuratorContext;

    return html`
      <p id="a11y-quantity-table-description" class="sr-only">
        ${this.a11yDescription}
      </p>
      <table
        aria-describedby="a11y-quantity-table-description"
        class="quantities ${classMap({
          collapsible: Boolean(quantityCollapsible),
        })}"
      >
        <thead>
          <tr>
            <th class="amount">${t('labels.quantity')}</th>
            <th>${t('labels.discount')}</th>
            <th class="mobile-hidden">${t('labels.pricePerItem')}</th>
            <th class="mobile-show">${t('labels.pricePerItemMb')}</th>
            <th>${t('labels.total')}</th>
          </tr>
        </thead>

        <tbody>
          ${this.options &&
          this.options.map(option => {
            const {
              quantity,
              queryString,
              selected,
              totalPrice,
              unitPrice,
              volumeDiscountPercentage,
              hspAvailable,
            } = option;

            return html`
              <tr
                tabindex="0"
                role="button"
                aria-describedby="row-instructions"
                aria-label=${getQuantityA11yName(
                  quantityCollapsible
                    ? StepEnum.quantity_collapsible
                    : StepEnum.quantity,
                  {
                    quantity: quantity,
                    discountPercentage: volumeDiscountPercentage,
                    unitPrice: unitPrice,
                    totalPrice: totalPrice,
                    fastDelivery: hspAvailable,
                  },
                )}
                class="${classMap({
                  active: selected,
                  loading: this.loading,
                })}"
                @click=${() => this.selectOption(queryString, option)}
                @keydown=${(event: KeyboardEvent) => {
                  if (event.code === 'Enter' || event.code === 'Space') {
                    this.selectOption(queryString, option);
                  }
                }}
              >
                <td class="amount">
                  <span class="checkmark-wrapper">
                    <span class="icon-wrapper ${classMap({
                      collapsible: Boolean(quantityCollapsible),
                    })}">
                      <span class="checkmark ${classMap({
                        selected: selected,
                      })}">${iconCheckmark}</span>
                    </span>
                    <span class="text">${quantity}</span>
                    ${
                      hspAvailable
                        ? html`
                            <fg-hsp-label onlySkewedLabel></fg-hsp-label>
                          `
                        : nothing
                    }
                  </span>
                </td>
                <td>
                    ${
                      volumeDiscountPercentage
                        ? html`
                            <span class="discount-percentage">
                              ${volumeDiscountPercentage}%
                            </span>
                          `
                        : html`
                            <span>-</span>
                          `
                    }
                  </span>
                </td>
                <td><span class="unit-price">${unitPrice}</span></td>
                <td><span>${totalPrice}</span></td>
              </tr>
            `;
          })}
        </tbody>

        ${this.renderCustomQuantityOption()}
      </table>
      <p id="row-instructions" class="sr-only">
        ${t('labels.pressEnterOrSpaceToActivateSelectedRow')}
      </p>
    `;
  }

  selectOption(
    queryString: QuantityOptionDto['queryString'],
    option: QuantityOptionDto,
  ) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );

    announceQuantityChange(
      this,
      this.configuratorContext.quantityCollapsible
        ? StepEnum.quantity_collapsible
        : StepEnum.quantity,
      {
        quantity: option?.quantity,
        fastDelivery: option?.hspAvailable,
        discountPercentage: option?.volumeDiscountPercentage,
        unitPrice: option?.unitPrice,
        totalPrice: option?.totalPrice,
      },
    );
  }

  setCustomQuantity(event: KeyboardEvent) {
    const eventTarget = event.target as HTMLInputElement;
    let quantity = parseFloat(getNumberValueFromString(eventTarget.value));

    this.firstInteracted = true;

    if (isNaN(quantity)) {
      quantity = 0;
    }

    this.customQuantity = {
      ...this.customQuantity,
      value: quantity,
    };

    this.debounceDispatchSetQuantity();
  }

  debounceDispatchSetQuantity = debounce(() => {
    this.dispatchEvent(
      new CustomEvent('quantity-set', {
        composed: true,
        detail: {
          quantity: this.customQuantity.value,
          queryString:
            this.customQuantityOption &&
            this.customQuantityOption.queryString + this.customQuantity.value,
        },
      }),
    );
  }, 1000);

  static styles = [
    baseStyles,
    formStyles,
    quantityTableStyles,
    loadingStyles,
    css`
      .quantities tbody tr {
        cursor: pointer;
        &:hover {
          background-color: var(
            --fg-tertiary--light,
            var(--fg-fallback-tertiary--light)
          );
          .amount .text {
            text-decoration: underline;
          }
        }
      }

      .quantities.collapsible th.amount {
        padding-left: 32px;
      }

      .quantities td span.discount-percentage {
        color: var(--fg-message-success, var(--fg-fallback-message-success));
        font-size: 1.125rem;
        font-weight: 700;
      }

      .custom-quantity-input {
        width: 75px;
      }

      .custom-quantity-input-td {
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: table-cell;
      }

      .unit-price {
        font-weight: 700;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .custom-quantity-input {
          width: 111px;
        }

        .quantities-custom-input td {
          padding-top: 4px;
          padding-bottom: 4px;
        }

        .mobile-show {
          display: none;
        }

        .mobile-hidden {
          display: table-cell;
        }
      }

      .setup-price td {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .setup-price td:first-child {
        text-align: left;
      }

      .setup-price td:last-child {
        font-weight: 700;
      }

      .quantities tfoot > tr > td.custom-quantity-input-td {
        padding-top: 4px;
        padding-bottom: 4px;
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .custom-quantity-input {
          width: 135px;
        }
      }
    `,
  ];
}
